import { ChangeEvent, useCallback, useEffect, useRef } from "react";
import { DropDownForm } from "src/components/common/form/DropdownForm";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import useUiuxStore from "src/store/useUiuxStore";


import { getItemPopupList } from "src/apis/itemApi";
import { BRDiv } from "src/components/common/common";
import { CodeForm } from "src/components/common/form/CodeForm";
import { InputForm } from "src/components/common/form/input/InputForm";
import { useInfoFormEvents } from "src/hooks/useInfoFormEvents";
import { InfoCodeSearchFieldConfig } from "src/types/info-form";
import { PRD3012_R01HeaderDto, PRD3012_R01LotDto } from "./Dto_PRD3012_R01";
import { usePRD3012_R01Store } from "./Store_PRD3012_R01";



interface Props {
	editable: boolean

	defaultPRD3012_R01HeaderRow: PRD3012_R01HeaderDto;

	handleFetchBomProductList: ( itemCd: string, warehouseCd: string, logicCenter: string ) => void;
}

export const Info_PRD3012_R01 = (props: Props) => {

	const { editable, 
		defaultPRD3012_R01HeaderRow,
		handleFetchBomProductList,
	} = props;

	const { itemStatusList, workPlaceList } = useCommonCodeStore();
	const { setCodeSearchModalProps } = useUiuxStore();
	const { 
			editPRD3012_R01, setEditPRD3012_R01,
			isInitialLoad, setIsInitialLoad,
			PRD3012_R01LotList, setPRD3012_R01LotList,
	} = usePRD3012_R01Store();

	// Info 폼 코드 검색 필드 설정
	const infoFieldConfigs: InfoCodeSearchFieldConfig[] = [
		{
			fieldName: 'itemCd',
            category: "품목",
            focusId: "itemCd_search",
			responseMapping: {
				itemCd : 'itemCd',
				itemNm : 'itemNm',
				itemSpec : 'itemSpec',
				itemUnit : 'itemUnit'
			},
			searchFunction: async (code: string) => {
				const employeeResponse = await getItemPopupList(code, '', '', '');
				return employeeResponse.data.data.find(value => value.itemCd === code);
			}
        },
	];

	// Info 폼 이벤트 훅 사용
	const { getFieldHandlers } = useInfoFormEvents({
		editData: editPRD3012_R01,
		setEditData: setEditPRD3012_R01,
		defaultData: defaultPRD3012_R01HeaderRow,
		infoFieldConfigs,
		setCodeSearchModalProps,
		formName: 'PRD3012_R01'
	});

	const containerRef = useRef<HTMLDivElement>(null);
	const infoRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		// 초기 로드 시에는 Content에서 이미 호출했으므로 건너뜁니다.
		if (isInitialLoad) {
			setIsInitialLoad(false);
			return;
		}

		if(editPRD3012_R01?.itemCd && editPRD3012_R01?.outWarehouseCd && editPRD3012_R01?.logicCenter) {
			handleFetchBomProductList(editPRD3012_R01.itemCd, editPRD3012_R01.outWarehouseCd, editPRD3012_R01.logicCenter);
		}
	}, [editPRD3012_R01?.outWarehouseCd, editPRD3012_R01?.logicCenter])

	const handleChange = useCallback((e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLSelectElement>) => {
		const { name, value } = e.target;

		let closeTag = editPRD3012_R01?.closeTag || 'N';
		if(Number(editPRD3012_R01?.orderQty) === Number(editPRD3012_R01?.productQty)) {
			closeTag = 'Y';
		}else {
			closeTag = 'N';
		}

		//상태값이 바뀌면 lot리스트의 itemStatus의 값도 바뀌어야 함
		if(name === 'itemStatus') {
			const newLotList = PRD3012_R01LotList.map((item: PRD3012_R01LotDto) => ({
				...item,
				itemStatus: value as string
			}));
			setPRD3012_R01LotList(newLotList);
		}

		setEditPRD3012_R01({
			...defaultPRD3012_R01HeaderRow,
			...editPRD3012_R01,
			[name]: value,
			closeTag: closeTag
		});
	}, [editPRD3012_R01, defaultPRD3012_R01HeaderRow, PRD3012_R01LotList, setEditPRD3012_R01, setPRD3012_R01LotList]);

	const inputFormDefault = {
		wrapperStyle: "w-[300px] min-w-[300px] mr-[10px] mb-[5px]",
		titleStyle: "w-[100px] min-w-[100px] text-left",
		codeInputStyle: "w-[200px] min-w-[200px]",
		custNmInputStyle: "w-[150px] min-w-[150px]"
	}

	return (
		<div className="w-full h-full flex flex-col gap-[4px]" ref={containerRef}>
				<div
					className="h-full max-h-full flex flex-wrap content-start overflow-y-auto"
					ref={infoRef}
				>
					<InputForm
						title="작업지시"
						value={editPRD3012_R01?.productOrderNo || ""}
						editable={false}
						name="productOrderNo"
						onChange={handleChange}
					/>
					<BRDiv />
					<DropDownForm
						title="작업정보"
						name="workplaceCd"
						initLabelLess={true}
						value={editPRD3012_R01?.workplaceCd}
						data={workPlaceList}
						editable={false}
						onChange={handleChange}
					/>
					<BRDiv />
					<CodeForm
						label="품목코드"
						value={editPRD3012_R01?.itemCd || ""}
						custNmValue={editPRD3012_R01?.itemNm || ""}
						editable={false}
						name="itemCd"
						handleChange={
							() => { }
							// handleChange
						}
						onDoubleClick={getFieldHandlers('itemCd').onDoubleClick}
						onKeyDown={() => { 
							// getFieldHandlers('itemCd').onEnterKeyDown
						}}
						custNmInputless={true}
						wrapperOption={inputFormDefault}
					/>
					<InputForm title="품명" value={editPRD3012_R01?.itemNm || ""}
						editable={false}
						name="itemNm"
						onChange={handleChange} />

					<BRDiv />
					<InputForm title="규격" value={editPRD3012_R01?.itemSpec || ""}
						editable={false}
						name="itemSpec"
						onChange={handleChange} />
					<BRDiv />
					<InputForm title="단위" value={editPRD3012_R01?.itemUnit || ""}
						editable={false}
						name="itemUnit"
						onChange={handleChange} />
					<BRDiv />
					<InputForm title="작업지시잔량" value={editPRD3012_R01?.orderQty || 0}
						editable={false}
						name="orderQty"
						onChange={handleChange} />
					<BRDiv />
					{/* <InputForm title="작업완료량" value={editPRD3012_R01?.resultQty || 0}
						editable={false}
						name="resultQty"
						onChange={handleChange} />
					<BRDiv /> */}
					<InputForm title="생산양품량" value={editPRD3012_R01?.productQty || 0}
						editable={false}
						name="productQty"
						onChange={handleChange} />
					<BRDiv />
					<DropDownForm
						title="등급"
						name="itemStatus"
						initLabelLess={true}
						value={editPRD3012_R01?.itemStatus}
						data={itemStatusList}
						editable={true}
						onChange={handleChange}
					/>
					<BRDiv />

				</div>
		</div>
	)
}
